import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SCRIPT_URL = import.meta.env.VITE_VISITORS_SCRIPT_URL;

// Try browser GPS → reverse geocode with Nominatim (no API key needed)
async function getBrowserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}`,
            { headers: { "Accept-Language": "en" } }
          );
          if (!res.ok) return resolve(null);
          const d = await res.json();
          resolve({
            country_name: d.address?.country,
            city: d.address?.city || d.address?.town || d.address?.village,
            region: d.address?.state,
            country_code: d.address?.country_code?.toUpperCase(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        } catch {
          resolve(null);
        }
      },
      () => resolve(null),
      { timeout: 5000, maximumAge: 60000 }
    );
  });
}

async function getLocation() {
  const apis = [
    {
      url: "https://ipwho.is/",
      map: (d) =>
        d.success
          ? {
              country_name: d.country,
              city: d.city,
              region: d.region,
              country_code: d.country_code,
              timezone: d.timezone?.id,
            }
          : null,
    },
    {
      url: "https://ipapi.co/json/",
      map: (d) => ({
        country_name: d.country_name,
        city: d.city,
        region: d.region,
        country_code: d.country_code,
        timezone: d.timezone,
      }),
    },
    {
      url: "https://freeipapi.com/api/json",
      map: (d) => ({
        country_name: d.countryName,
        city: d.cityName,
        region: d.regionName,
        country_code: d.countryCode,
        timezone: d.timeZone,
      }),
    },
    {
      url: "https://ipinfo.io/json",
      map: (d) => ({
        country_name: d.country,
        city: d.city,
        region: d.region,
        country_code: d.country,
        timezone: d.timezone,
      }),
    },
  ];

  for (const api of apis) {
    try {
      const res = await fetch(api.url);
      if (!res.ok) continue;
      const data = await res.json();
      const mapped = api.map(data);
      if (mapped?.country_name) return mapped;
    } catch {
      // try next
    }
  }
  return null;
}

// Human signals: any real user will trigger at least one of these
const HUMAN_EVENTS = ["mousemove", "scroll", "click", "keydown", "touchstart"];

function waitForHuman(timeout = 15000) {
  return new Promise((resolve) => {
    // Additional checks: real browsers have these properties
    const looksLikeBot =
      !navigator.cookieEnabled ||
      navigator.webdriver === true ||
      /bot|crawl|spider|slurp|yahoo|baiduspider|facebookexternalhit/i.test(navigator.userAgent);

    if (looksLikeBot) return resolve(false);

    let resolved = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      HUMAN_EVENTS.forEach((e) => window.removeEventListener(e, done));
      resolve(true);
    };
    HUMAN_EVENTS.forEach((e) => window.addEventListener(e, done, { passive: true, once: true }));
    // Give up after timeout — don't track at all if no interaction
    setTimeout(() => {
      if (!resolved) { resolved = true; resolve(false); }
    }, timeout);
  });
}

export function useVisitorTracking() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!SCRIPT_URL) return;

    const sessionKey = `tracked_${pathname}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    waitForHuman().then((isHuman) => {
      if (!isHuman) return; // bot detected or no interaction — skip tracking

      sessionStorage.setItem(sessionKey, "1");

      Promise.race([
        getBrowserLocation(),
        new Promise((r) => setTimeout(() => r(null), 6000)),
      ]).then((gps) => (gps?.country_name ? gps : getLocation())).then((loc) => {
        const payload = {
          timestamp: new Date().toISOString(),
          page: pathname || "/",
          country: loc?.country_name || "Unknown",
          city: loc?.city || "Unknown",
          region: loc?.region || "Unknown",
          countryCode: loc?.country_code || "??",
          device: isMobile ? "Mobile" : "Desktop",
          timezone: loc?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          referrer: document.referrer || "Direct",
        };

        // mode: 'no-cors' is required for Google Apps Script POST (avoids preflight)
        // Content-Type: 'text/plain' prevents a preflight; data arrives in e.postData.contents
        fetch(SCRIPT_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain" },
          body: JSON.stringify(payload),
        }).catch(() => {});
      });
    });
  }, [pathname]);
}
